import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Ticket } from 'lucide-react';
import { DiscountType, UserRole } from '@kick/types';
import { api, getErrorMessage } from '@/lib/api';
import { cn, formatCOP } from '@/lib/cn';
import { useAuth } from '@/lib/auth-store';
import { usePuede } from '@/lib/permisos';
import {
  Boton,
  Campo,
  Cargando,
  ConfirmarModal,
  Entrada,
  EntradaDinero,
  ErrorCarga,
  Modal,
  Seleccion,
  Vacio,
} from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface Cupon {
  id: string;
  code: string;
  description: string | null;
  discountType: DiscountType;
  value: number;
  minOrderAmount: number;
  maxDiscount: number | null;
  merchantId: string | null;
  merchantName: string | null;
  validFrom: string;
  validUntil: string;
  usageLimit: number | null;
  usageLimitPerUser: number | null;
  usedCount: number;
  isActive: boolean;
}

const TIPOS = [
  { valor: DiscountType.PERCENTAGE, texto: 'Porcentaje' },
  { valor: DiscountType.FIXED_AMOUNT, texto: 'Monto fijo' },
  { valor: DiscountType.FREE_DELIVERY, texto: 'Domicilio gratis' },
];

/**
 * Cupones.
 *
 * El administrador crea los de la plataforma —valen en cualquier comercio— y
 * el comercio los suyos. Un comercio ve los globales pero no los toca: le
 * sirven para entender por qué un cliente pagó menos, no para apagar una
 * promoción que no es suya.
 */
export function Cupones() {
  const puede = usePuede();
  const { user, merchantId } = useAuth();
  const admin = user?.role === UserRole.ADMIN;
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [creando, setCreando] = useState(false);
  const [desactivando, setDesactivando] = useState<Cupon | null>(null);

  const lista = useQuery({
    queryKey: ['cupones'],
    queryFn: async () => (await api.get<Cupon[]>('/cupones')).data,
  });

  const desactivar = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/cupones/${id}`)).data,
    onSuccess: () => {
      avisos.exito('Cupón desactivado');
      setDesactivando(null);
      void cliente.invalidateQueries({ queryKey: ['cupones'] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  if (lista.isLoading) return <Cargando />;
  if (lista.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(lista.error)} onReintentar={() => void lista.refetch()} />
    );
  }

  const cupones = lista.data ?? [];

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Cupones</h1>
          <p className="mt-1 text-sm text-kick-gray">
            {admin
              ? 'Los de la plataforma valen en cualquier comercio.'
              : 'Los tuyos y los de la plataforma, que también aplican a tus pedidos.'}
          </p>
        </div>
        {puede('cupones', 'crear') && (
          <Boton icono={<Plus className="h-4 w-4" />} onClick={() => setCreando(true)}>
            Crear cupón
          </Boton>
        )}
      </div>

      {cupones.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<Ticket className="h-6 w-6" />}
            titulo="Todavía no hay cupones"
            descripcion="Crea uno para que tus clientes tengan un motivo para volver."
          />
        </div>
      ) : (
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          {cupones.map((c) => (
            <Tarjeta
              key={c.id}
              cupon={c}
              /* Un comercio no desactiva los globales, aunque los vea. */
              editable={
                admin
                  ? puede('cupones', 'editar')
                  : !!c.merchantId && c.merchantId === merchantId
              }
              onDesactivar={() => setDesactivando(c)}
            />
          ))}
        </div>
      )}

      {creando && (
        <FormularioCupon
          admin={admin}
          onCerrar={() => setCreando(false)}
          onCreado={() => {
            setCreando(false);
            void cliente.invalidateQueries({ queryKey: ['cupones'] });
          }}
        />
      )}

      <ConfirmarModal
        abierto={!!desactivando}
        titulo={`Desactivar ${desactivando?.code ?? ''}`}
        mensaje="Deja de aceptarse de inmediato. Los pedidos que ya lo usaron no cambian."
        textoConfirmar="Desactivar"
        cargando={desactivar.isPending}
        onConfirmar={() => desactivando && desactivar.mutate(desactivando.id)}
        onCancelar={() => setDesactivando(null)}
      />
    </div>
  );
}

function Tarjeta({
  cupon: c,
  editable,
  onDesactivar,
}: {
  cupon: Cupon;
  editable: boolean;
  onDesactivar: () => void;
}) {
  const vencido = new Date(c.validUntil) < new Date();
  const agotado = c.usageLimit != null && c.usedCount >= c.usageLimit;
  const vivo = c.isActive && !vencido && !agotado;

  return (
    <section className={cn('card-kick p-5', !vivo && 'opacity-60')}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-display text-xl tracking-wide text-kick-dark">{c.code}</p>
          <p className="text-sm text-kick-gray">{descuento(c)}</p>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-xs font-bold',
            vivo ? 'bg-emerald-100 text-emerald-800' : 'bg-kick-gray/15 text-kick-gray',
          )}
        >
          {!c.isActive ? 'Desactivado' : vencido ? 'Vencido' : agotado ? 'Agotado' : 'Activo'}
        </span>
      </div>

      {c.description && <p className="mt-2 text-sm text-kick-ink">{c.description}</p>}

      <dl className="mt-3 space-y-1 border-t border-kick-gray/15 pt-3 text-sm">
        <Dato etiqueta="Alcance" valor={c.merchantName ?? 'Toda la plataforma'} />
        {c.minOrderAmount > 0 && (
          <Dato etiqueta="Mínimo" valor={formatCOP(c.minOrderAmount)} />
        )}
        <Dato
          etiqueta="Vigencia"
          valor={`hasta ${new Date(c.validUntil).toLocaleDateString('es-CO', {
            day: 'numeric',
            month: 'short',
            timeZone: 'America/Bogota',
          })}`}
        />
        <Dato
          etiqueta="Usos"
          valor={c.usageLimit ? `${c.usedCount} de ${c.usageLimit}` : `${c.usedCount}`}
        />
      </dl>

      {editable && c.isActive && (
        <div className="mt-4">
          <Boton variante="peligro" className="px-3 py-1.5 text-xs" onClick={onDesactivar}>
            Desactivar
          </Boton>
        </div>
      )}
    </section>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-kick-gray">{etiqueta}</dt>
      <dd className="text-right font-semibold text-kick-ink">{valor}</dd>
    </div>
  );
}

function descuento(c: Cupon): string {
  if (c.discountType === DiscountType.FREE_DELIVERY) return 'Domicilio gratis';
  if (c.discountType === DiscountType.PERCENTAGE) {
    return `${c.value} % de descuento${c.maxDiscount ? ` (máx. ${formatCOP(c.maxDiscount)})` : ''}`;
  }
  return `${formatCOP(c.value)} de descuento`;
}

function FormularioCupon({
  admin,
  onCerrar,
  onCreado,
}: {
  admin: boolean;
  onCerrar: () => void;
  onCreado: () => void;
}) {
  const avisos = useAvisos();
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [discountType, setDiscountType] = useState<DiscountType>(DiscountType.PERCENTAGE);
  const [value, setValue] = useState(10);
  const [minOrderAmount, setMinOrderAmount] = useState(0);
  const [maxDiscount, setMaxDiscount] = useState(0);
  const [dias, setDias] = useState(30);
  const [usageLimit, setUsageLimit] = useState('');
  const [usageLimitPerUser, setUsageLimitPerUser] = useState('1');
  const [errores, setErrores] = useState<Record<string, string[]>>({});

  const porcentaje = discountType === DiscountType.PERCENTAGE;
  const gratis = discountType === DiscountType.FREE_DELIVERY;

  const crear = useMutation({
    mutationFn: async () => {
      const desde = new Date();
      const hasta = new Date(desde.getTime() + dias * 24 * 60 * 60 * 1000);

      return (
        await api.post('/cupones', {
          code: code.trim().toUpperCase(),
          ...(description.trim() ? { description: description.trim() } : {}),
          discountType,
          // El domicilio gratis no lleva importe: lo que descuenta es el envío.
          value: gratis ? 0 : value,
          minOrderAmount,
          ...(porcentaje && maxDiscount > 0 ? { maxDiscount } : {}),
          validFrom: desde.toISOString(),
          validUntil: hasta.toISOString(),
          ...(usageLimit ? { usageLimit: Number(usageLimit) } : {}),
          ...(usageLimitPerUser ? { usageLimitPerUser: Number(usageLimitPerUser) } : {}),
          isActive: true,
        })
      ).data;
    },
    onSuccess: () => {
      avisos.exito('Cupón creado');
      onCreado();
    },
    onError: (e) => {
      const detalles = (e as { response?: { data?: { details?: Record<string, string[]> } } })
        .response?.data?.details;
      if (detalles) setErrores(detalles);
      else avisos.error(getErrorMessage(e));
    },
  });

  return (
    <Modal abierto titulo="Cupón nuevo" onCerrar={onCerrar}>
      <div className="space-y-4">
        <Campo etiqueta="Código" requerido error={errores.code?.[0]}>
          <Entrada
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="BIENVENIDO"
            maxLength={40}
          />
        </Campo>

        <Campo etiqueta="Descripción">
          <Entrada
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="20 % en tu primer pedido"
            maxLength={200}
          />
        </Campo>

        <Campo etiqueta="Tipo de descuento" requerido>
          <Seleccion
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value as DiscountType)}
          >
            {TIPOS.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.texto}
              </option>
            ))}
          </Seleccion>
        </Campo>

        {!gratis &&
          (porcentaje ? (
            <Campo etiqueta="Porcentaje" requerido error={errores.value?.[0]}>
              <Entrada
                type="number"
                min={1}
                max={100}
                value={value}
                onChange={(e) => setValue(Number(e.target.value))}
              />
            </Campo>
          ) : (
            <Campo etiqueta="Monto del descuento" requerido>
              <EntradaDinero centavos={value} onCambio={setValue} />
            </Campo>
          ))}

        {porcentaje && (
          <Campo
            etiqueta="Tope del descuento"
            ayuda="Deja 0 para no poner tope. Sin él, un 20 % sobre un pedido grande se va de las manos."
          >
            <EntradaDinero centavos={maxDiscount} onCambio={setMaxDiscount} />
          </Campo>
        )}

        <Campo etiqueta="Pedido mínimo">
          <EntradaDinero centavos={minOrderAmount} onCambio={setMinOrderAmount} />
        </Campo>

        <Campo etiqueta="Días de vigencia" requerido>
          <Entrada
            type="number"
            min={1}
            max={365}
            value={dias}
            onChange={(e) => setDias(Number(e.target.value))}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Usos totales" ayuda="Vacío = sin límite">
            <Entrada
              type="number"
              min={1}
              value={usageLimit}
              onChange={(e) => setUsageLimit(e.target.value)}
              placeholder="Sin límite"
            />
          </Campo>
          <Campo etiqueta="Usos por persona" ayuda="Vacío = sin límite">
            <Entrada
              type="number"
              min={1}
              value={usageLimitPerUser}
              onChange={(e) => setUsageLimitPerUser(e.target.value)}
            />
          </Campo>
        </div>

        {admin && (
          <p className="rounded-kick bg-kick-light px-3 py-2 text-sm text-kick-ink">
            Este cupón será de toda la plataforma: cualquier cliente podrá usarlo en cualquier
            comercio.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            onClick={() => crear.mutate()}
            cargando={crear.isPending}
            disabled={code.trim().length < 3}
          >
            Crear cupón
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
